import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Column } from '@/types/common';
import {
  OUTBOUND_STATUSES,
  OutboundOrder,
  OutboundOrderStatus,
  warehouseApi,
  warehouseExport,
} from '@/services/api/warehouse';
import {
  CARRIER_LABEL,
  codeCls,
  ExportCsvButton,
  fmtDate,
  fmtDateTime,
  fmtQty,
  HubCell,
  labelOf,
  ListCard,
  ListTab,
  metaCls,
  nameCls,
  OUTBOUND_SOURCE_LABEL,
  OUTBOUND_STATUS,
  PageHead,
  StatusBadge,
  useListState,
  useLoad,
  WAREHOUSE_PATHS,
} from './shared';
import WarehouseFilter from './WarehouseFilter';
import { OUTBOUND_FILTERS } from './filterFields';

type Tab = 'ALL' | OutboundOrderStatus;

// Fits 1280px without scrolling: source sits under the external ID, the
// two dates share a cell, and method (nearly always Delivery) is on the
// detail page only.
const COLUMNS: Column<OutboundOrder>[] = [
  {
    key: 'code',
    title: 'Request ID',
    sticky: 'left',
    render: (_v, o) => (
      <div>
        <p className="font-mono text-xs font-semibold text-primary">{o.code}</p>
        <p className={`${metaCls} max-w-[150px] truncate`}>
          <span className="font-mono">{o.externalID}</span> ·{' '}
          {labelOf(OUTBOUND_SOURCE_LABEL, o.source)}
        </p>
      </div>
    ),
  },
  {
    key: 'recipient',
    title: 'Recipient',
    render: (_v, o) => (
      <div className="max-w-[120px]">
        <p className={`${nameCls} truncate`} title={o.recipientName}>
          {o.recipientName}
        </p>
        <p
          className={`${metaCls} truncate`}
          title={[o.destinationCity, o.destinationAddress]
            .filter(Boolean)
            .join(' · ')}
        >
          {o.destinationCity ? `${o.destinationCity} · ` : ''}
          {o.destinationAddress}
        </p>
      </div>
    ),
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, o) => (
      <div className="text-right">
        <p className="text-sm tabular-nums text-text-strong-950">
          {fmtQty(o.totalQty)}
        </p>
        <p className={metaCls}>{o.lineCount} SKU</p>
      </div>
    ),
  },
  {
    key: 'hub',
    title: 'Hub Name',
    render: (_v, o) => <HubCell hub={o.hub} hubID={o.hubID} />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (_v, o) => <StatusBadge map={OUTBOUND_STATUS} value={o.status} />,
  },
  {
    key: 'waybill',
    title: 'Waybill / Kurir',
    // The waybill in full on one line, the courier on the line under it.
    // A handover order has neither — it says so instead of "—".
    render: (_v, o) => {
      const f = o.fulfillment;
      const courier =
        o.courier?.name ??
        (f?.carrier && f.carrier !== 'DASH'
          ? labelOf(CARRIER_LABEL, f.carrier)
          : null);
      if (!f?.waybill && !courier) return <span className={metaCls}>—</span>;
      return (
        <div>
          {f?.waybill && (
            <p className={`${codeCls} whitespace-nowrap`}>{f.waybill}</p>
          )}
          {courier && (
            <p
              className={`${metaCls} max-w-[200px] truncate`}
              title={
                o.courier ? `${o.courier.name} · ${o.courier.code}` : courier
              }
            >
              <i
                className={`${f?.carrier === 'HANDOVER' ? 'ri-hand-heart-line' : 'ri-e-bike-2-line'} mr-1`}
              />
              {courier}
            </p>
          )}
        </div>
      );
    },
  },
  {
    key: 'dates',
    title: 'Tanggal',
    render: (_v, o) => (
      <div>
        <p className="text-xs text-text-sub-600">
          Exp. {fmtDate(o.expectedDeliveryDate)}
        </p>
        <p className={metaCls} title={fmtDateTime(o.createdAt)}>
          Dibuat {fmtDate(o.createdAt)}
        </p>
      </div>
    ),
  },
];

const OutboundListPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useListState<Tab>('ALL');
  const search = q.search || undefined;

  const list = useLoad(
    () =>
      warehouseApi.listOutboundOrders(
        {
          status: q.tab === 'ALL' ? undefined : q.tab,
          search,
          page: q.page,
          limit: q.limit,
        },
        q.filters
      ),
    [q.tab, search, q.filterKey, q.page, q.limit]
  );
  // Tab counts: the list's search + filters, minus the tab — never disagree
  // with it.
  const summary = useLoad(
    () => warehouseApi.getOutboundSummary({ search }, q.filters),
    [search, q.filterKey]
  );

  const tabs: ListTab<Tab>[] = (['ALL', ...OUTBOUND_STATUSES] as Tab[]).map(
    (k) => ({
      key: k,
      label: k === 'ALL' ? 'All' : OUTBOUND_STATUS[k].label,
      count: summary.data
        ? k === 'ALL'
          ? summary.data.total
          : (summary.data.byStatus.find((b) => b.status === k)?.count ?? 0)
        : null,
    })
  );

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <PageHead
        title="Outbound request"
        description="Order keluar dari gudang Dash ke penerima Anda — dari picking sampai terkirim."
        actions={
          <ExportCsvButton
            what="Outbound request"
            onExport={() =>
              warehouseExport.outboundOrders(
                { search, status: q.tab === 'ALL' ? undefined : q.tab },
                q.filters
              )
            }
          />
        }
      />
      <ListCard<OutboundOrder, Tab>
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        searchPlaceholder="Cari request ID, external ID, penerima, waybill…"
        onSearch={q.setSearch}
        filter={
          <WarehouseFilter
            title="Outbound request"
            fields={OUTBOUND_FILTERS}
            value={q.filters}
            onApply={q.setFilters}
          />
        }
        columns={COLUMNS}
        rows={list.data?.data ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        emptyTitle="Belum ada outbound request"
        onRowClick={(o) => navigate(`${WAREHOUSE_PATHS.outbound}/${o.id}`)}
        page={q.page}
        limit={q.limit}
        total={list.data?.pagination.total ?? 0}
        onPageChange={q.setPage}
        onLimitChange={q.setLimit}
      />
    </div>
  );
};

export default OutboundListPage;
